"use client";

import React, { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import {  Box, Heading, Flex } from "@radix-ui/themes";
import DialogLayoutView from "./dialogView/dialogLayout";

interface KeyboardShortcutProps {
  triggerComponent?: React.ReactNode | null;
}

interface ShortcutConfig {
  key: string;
  route: string;
  description: string;
}

const SHORTCUTS_CONFIG: ShortcutConfig[] = [
  { key: "d", route: "/dashboard", description: "Go to Dashboard" },
  { key: "p", route: "/patient", description: "Go to Patients" },
  { key: "u", route: "/updates", description: "Go to Update" },
];

const MODAL_SHORTCUTS = [
  { key: "?", description: "Show this help" },
  { key: "Esc", description: "Close this help" },
];

const KeyboardShortcuts: React.FC<KeyboardShortcutProps> = (props) => {
  const router = useRouter();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      // Ignore key presses in input fields
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      // Handle modal controls
      if (event.key === "?") {
        event.preventDefault();
        setShowModal(true);
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        setShowModal(false);
        return;
      }

      // Don't handle navigation shortcuts when modal is open
      if (showModal) {
        return;
      }

      // Handle navigation shortcuts
      const shortcut = SHORTCUTS_CONFIG.find(
        (config) => config.key === event.key.toLowerCase()
      );

      if (shortcut) {
        event.preventDefault();
        router.push(shortcut.route);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [router, showModal]);

  return (
    <>
      <DialogLayoutView
        enableFullWidthTrigger={false}
        headerContent={
          <Flex className="flex-col gap-2">
            <Heading size={{ initial: "3", xl: "4" }} weight="medium">
              Keyboard Shortcuts
            </Heading>
            <Heading className="text-sm text-gray-500 font-normal">
              Use these keyboard shortcuts for quick navigation
            </Heading>
          </Flex>
        }
        enableDialogView={showModal}
        handleDialogViewChange={(isOpen: boolean) => setShowModal(isOpen)}
        handleDialogCloseAction={() => {}}
        triggerContent={props?.triggerComponent || null}
        enableCloseAction={true}
        contentClassValue={
          "min-w-64 !px-5 !pt-8 !pb-5 !border-none !shadow-dropdown !max-w-[685px] !overflow-x:hidden"
        }
        content={
          <Box className="w-full h-full gap-5">
            {/* <Dialog.Description className="!mb-4 text-gray-500">
              Use these keyboard shortcuts for quick navigation
            </Dialog.Description> */}

            <table className="w-full">
              <thead>
                <tr>
                  <th className="p-2 text-left">Key</th>
                  <th className="p-2 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {SHORTCUTS_CONFIG.map((shortcut) => (
                  <tr key={shortcut.key}>
                    <td className="p-2">
                      <kbd>{shortcut.key.toUpperCase()}</kbd>
                    </td>
                    <td className="p-2">{shortcut.description}</td>
                  </tr>
                ))}
                {MODAL_SHORTCUTS.map((shortcut) => (
                  <tr key={shortcut.key}>
                    <td className="p-2">
                      <kbd>{shortcut.key}</kbd>
                    </td>
                    <td className="p-2">{shortcut.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        }
      />
    </>
  );
};

export default KeyboardShortcuts;
